import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { FaBook, FaEye, FaList, FaArrowLeft } from "react-icons/fa";
import styles from "./Book.module.css";
import { useSelector, useDispatch } from "react-redux";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../services/Api";
import {
  CustomButton,
  CustomDropdown,
  CustomFileInput,
  CustomTextInput,
} from "../../../components";
import CustomProgressBar from "../../../components/Customprogressbar/Customprogressbar";
import PopupModal from "../../../components/PopupModal/PopupModal";
import PDFModal from "../../../components/CustomModal/Pdfmodal";
import { fetchClassOptions } from "../../../redux/Masterdata";

// ─────────────────────────────────────────────
//  Shared inline styles for the editable TOC cells
// ─────────────────────────────────────────────
const tocInputStyle = {
  width: "100%",
  border: "none",
  padding: "4px 0",
  fontSize: "var(--font-size-sm)",
  fontFamily: "var(--font-family-primary)",
  background: "transparent",
  outline: "none",
};
const tocNumInputStyle = {
  ...tocInputStyle,
  width: "70px",
  textAlign: "center",
};
// const errorTextStyle = { color: "red", fontSize: "12px", marginTop: "4px" };

const BookCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, translations } = useSelector((state) => state.language || {});

  // ==================== USER / AUTH DATA ====================
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const user_id = user?.id || "";

  // ==================== REDUX STATE ====================
  const { classOptions: reduxClassOptions } = useSelector(
    (state) => state.masterData,
  );

  const classOptions = useMemo(
    () => [
      { value: "", label: t?.selectClass || "Select Class" },
      ...(reduxClassOptions || []),
    ],
    [reduxClassOptions, t],
  );

  // ==================== STEPPER STATE ====================
  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  const stepperSteps = useMemo(
    () => [
      { step: 1, label: t?.addBook || "Add Book" },
      {
        step: 2,
        label: t?.generateTableOfContents || "Generate Table of Contents",
      },
    ],
    [t],
  );

  // ==================== FORM STATE ====================
  const [formData, setFormData] = useState({
    class_id: "",
    className: "",
    subject: "",
    subjectName: "",
    medium: "",
    term: "",
    volume: "",
    content_start_page: "",
    content_end_page: "",
    book_page_start: "1",
    pdf_page_start: "",
    book_pdf: null,
    book_pdf_original_name: "",
    uploaded_file_path: "",
  });

  const [errors, setErrors] = useState({});
  const [generateLoading, setGenerateLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [classSubjectList, setClassSubjectList] = useState([]);
  const [tocData, setTocData] = useState(null);
  const [bookId, setBookId] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  // ==================== PDF MODAL STATE ====================
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfModalUrl, setPdfModalUrl] = useState("");
  const [pdfModalTitle, setPdfModalTitle] = useState("");

  // ==================== MODAL STATE ====================
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  // ==================== REFS FOR CANCEL BOOK API ====================
  const bookIdRef = useRef(null);
  const activeStepRef = useRef(1);
  const isSubmittedRef = useRef(false);

  useEffect(() => {
    bookIdRef.current = bookId;
  }, [bookId]);

  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  // ==================== MODAL HANDLERS ====================
  const handleSuccessOk = () => {
    const { redirectPath } = modalState;
    setModalState({ open: false, type: "", message: "", redirectPath: "" });

    if (redirectPath && typeof redirectPath === "object" && redirectPath.path) {
      navigate(redirectPath.path, {
        state: redirectPath.state,
        replace: redirectPath.replace || false,
      });
    } else if (typeof redirectPath === "string" && redirectPath.trim() !== "") {
      isSubmittedRef.current = true;
      bookIdRef.current = null;

      if (activeStepRef.current === 2) {
        window.history.back();
        setTimeout(() => navigate(redirectPath), 50);
      } else {
        navigate(redirectPath);
      }
    }
  };

  const handleModalClose = () => {
    const { redirectPath } = modalState;
    setModalState({ open: false, type: "", message: "", redirectPath: "" });

    if (
      redirectPath &&
      typeof redirectPath === "string" &&
      redirectPath.trim() !== ""
    ) {
      isSubmittedRef.current = true;
      bookIdRef.current = null;

      if (activeStepRef.current === 2) {
        window.history.back();
        setTimeout(() => navigate(redirectPath), 50);
      } else {
        navigate(redirectPath);
      }
    }
  };

  // ==================== DROPDOWN OPTIONS ====================
  const mediumOptions = useMemo(
    () => [
      { value: "", label: t?.selectMedium || "Select Medium" },
      { value: "EM", label: "EM" },
      { value: "TM", label: "TM" },
    ],
    [t],
  );

  const termOptions = useMemo(
    () => [
      { value: "", label: t?.selectTerm || "Select Term" },
      { value: "Term 1", label: "Term 1" },
      { value: "Term 2", label: "Term 2" },
      { value: "Term 3", label: "Term 3" },
    ],
    [t],
  );

  const volumeOptions = useMemo(
    () => [
      { value: "", label: t?.selectVolume || "Select Volume" },
      { value: "Volume 1", label: "Volume 1" },
      { value: "Volume 2", label: "Volume 2" },
    ],
    [t],
  );

  // ==================== MONTH OPTIONS (January → December) ====================
  const monthOptions = useMemo(
    () => [
      { value: "", label: t?.selectMonth || "Select Month" },
      { value: "January", label: t?.january || "January" },
      { value: "February", label: t?.february || "February" },
      { value: "March", label: t?.march || "March" },
      { value: "April", label: t?.april || "April" },
      { value: "May", label: t?.may || "May" },
      { value: "June", label: t?.june || "June" },
      { value: "July", label: t?.july || "July" },
      { value: "August", label: t?.august || "August" },
      { value: "September", label: t?.september || "September" },
      { value: "October", label: t?.october || "October" },
      { value: "November", label: t?.november || "November" },
      { value: "December", label: t?.december || "December" },
    ],
    [t],
  );

  const subjectOptions = useMemo(
    () => [
      { value: "", label: t?.selectsubjectPlaceholder || "Select subject" },
      ...classSubjectList.map((item) => ({
        value: item.value,
        label: item.label || "",
      })),
    ],
    [classSubjectList, t],
  );

  // ==================== CANCEL BOOK API ====================
  const cancelBookApi = useCallback(
    async (defId) => {
      if (!defId) return { success: false, message: "" };
      try {
        const response = await AxiosInstance.delete("/book/cancelBook", {
          data: {
            def_id: Number(defId),
            user_id: Number(user_id),
          },
        });
        return {
          success: response?.data?.status === true,
          message: response?.data?.message || "",
        };
      } catch (error) {
        console.error("Cancel book error:", error);
        return { success: false, message: "" };
      }
    },
    [user_id],
  );

  const cancelBookOnUnload = useCallback(
    (defId) => {
      if (!defId) return;
      try {
        const url = `${AxiosInstance.defaults.baseURL || ""}/book/cancelBook`;
        const token = TokenService.getToken ? TokenService.getToken() : null;

        fetch(url, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            def_id: Number(defId),
            user_id: Number(user_id),
          }),
          keepalive: true,
        }).catch((err) => console.error("Cancel book unload error:", err));
      } catch (error) {
        console.error("Cancel book unload error:", error);
      }
    },
    [user_id],
  );

  // ==================== API CALLS ====================
  const fetchClassSubjectList = useCallback(
    async (classId) => {
      try {
        const response = await AxiosInstance.post(`/school/class/subjects`, {
          class_id: classId,
          section: "",
          shift_id: "",
          school_id: school_id,
        });
        if (response?.data?.success) {
          const apiData = response.data.data || [];
          setClassSubjectList(
            apiData.map((item) => ({
              id: item.id,
              class_id: item.class_id,
              class: item.section_id,
              label: item.subject_name,
              value: item.subject_id,
            })),
          );
        } else {
          setClassSubjectList([]);
        }
      } catch (error) {
        console.error("Error fetching subject list:", error);
        setClassSubjectList([]);
      }
    },
    [school_id],
  );

  // ==================== EFFECTS ====================
  useEffect(() => {
    if (school_id && academic_year) {
      dispatch(
        fetchClassOptions({ id: school_id, academic_year: academic_year }),
      );
    }
  }, [dispatch, school_id, academic_year]);

  useEffect(() => {
    if (formData.class_id) {
      fetchClassSubjectList(formData.class_id);
    } else {
      setClassSubjectList([]);
      setFormData((prev) => ({ ...prev, subject: "", subjectName: "" }));
    }
  }, [formData.class_id, fetchClassSubjectList]);

  // ==================== BROWSER BACK + RELOAD HANDLERS ====================
  useEffect(() => {
    if (activeStep === 2 && bookId) {
      window.history.pushState({ bookCreateStep: 2 }, "");
    }
  }, [activeStep, bookId]);

  useEffect(() => {
    const handlePopState = () => {
      if (
        activeStepRef.current === 2 &&
        bookIdRef.current &&
        !isSubmittedRef.current
      ) {
        cancelBookApi(bookIdRef.current);
        bookIdRef.current = null;
        navigate(-1);
      }
    };

    const handleBeforeUnload = () => {
      if (
        activeStepRef.current === 2 &&
        bookIdRef.current &&
        !isSubmittedRef.current
      ) {
        cancelBookOnUnload(bookIdRef.current);
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [cancelBookApi, cancelBookOnUnload, navigate]);

  // ==================== HANDLERS ====================
  const handleDropdownChange = (name, selectedOption) => {
    const value = selectedOption?.value || "";
    const label = selectedOption?.label || "";

    if (name === "class_id") {
      setFormData((prev) => ({
        ...prev,
        class_id: value,
        className: label,
        subject: "",
        subjectName: "",
      }));
      setErrors((prev) => ({ ...prev, class_id: "" }));
    } else if (name === "subject") {
      setFormData((prev) => ({ ...prev, subject: value, subjectName: label }));
      setErrors((prev) => ({ ...prev, subject: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const pageFields = [
      "content_start_page",
      "content_end_page",
      "book_page_start",
      "pdf_page_start",
    ];

    if (pageFields.includes(name) && value !== "" && !/^\d{0,4}$/.test(value)) {
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = useCallback(
    async (file) => {
      if (!file) {
        setFormData((prev) => ({
          ...prev,
          book_pdf: null,
          book_pdf_original_name: "",
          uploaded_file_path: "",
        }));
        setErrors((prev) => ({ ...prev, book_pdf: "" }));
        return;
      }

      if (file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          book_pdf: t?.onlyPdfFilesAllowed || "Only PDF files are allowed",
        }));
        setFileInputKey(Date.now());
        return;
      }

      const fileName = file.name
        .replace(/\.pdf$/i, "")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");

      const parts = fileName.split("_");

      const selectedClass = formData.className || "";
      const selectedSubject = formData.subjectName || "";
      const selectedMedium = formData.medium || "";

      const romanMap = { 1: "I", 2: "II", 3: "III" };
      const selectedTerm = formData.term
        ? formData.term
            .replace(/\s/g, "_")
            .replace(/_(\d)$/, (_, d) => "_" + (romanMap[d] || d))
        : "";

      const fileClass = parts[0] || "";
      const fileSubject = parts[1] || "";
      const fileMedium = parts[2] || "";
      const fileTerm = parts.slice(3).join("_") || "";

      const mismatches = [];

      if (
        selectedClass &&
        fileClass.toUpperCase() !== selectedClass.toUpperCase()
      ) {
        mismatches.push(t?.class || "Class");
      }
      if (
        selectedSubject &&
        fileSubject.toUpperCase() !== selectedSubject.toUpperCase()
      ) {
        mismatches.push(t?.subject || "Subject");
      }
      if (
        selectedMedium &&
        fileMedium.toUpperCase() !== selectedMedium.toUpperCase()
      ) {
        mismatches.push(t?.subjectMedium || "Subject Medium");
      }
      if (
        selectedTerm &&
        fileTerm.toUpperCase() !== selectedTerm.toUpperCase()
      ) {
        mismatches.push(t?.term || "Term");
      }

      if (mismatches.length > 0) {
        setModalState({
          open: true,
          type: "Failed",
          message:
            (t?.fileNameMismatch || "File name does not match the selected") +
            ": " +
            mismatches.join(", ") +
            ". " +
            (t?.expectedFileName || "Expected file name format") +
            ": " +
            `${selectedClass}_${selectedSubject}_${selectedMedium}${selectedTerm ? "_" + selectedTerm : ""}`,
          redirectPath: "",
        });
        setFileInputKey(Date.now());
        return;
      }

      setFileUploading(true);
      try {
        const payload = new FormData();
        payload.append("content_image", file);
        payload.append("type", "Books");
        payload.append("school_id", school_id);

        const response = await AxiosInstance.post(
          "/content/upload-book",
          payload,
          {
            headers: { "Content-Type": "multipart/form-data" },
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
          },
        );

        if (
          response?.data?.status ||
          response?.data?.success ||
          Array.isArray(response?.data)
        ) {
          const raw = response.data.data || response.data;
          const uploaded = Array.isArray(raw) ? raw[0] || {} : raw;
          setFormData((prev) => ({
            ...prev,
            book_pdf: file,
            book_pdf_original_name: uploaded.original_name || file.name,
            uploaded_file_path: uploaded.upload_path || "",
          }));
          setErrors((prev) => ({ ...prev, book_pdf: "" }));
        } else {
          setFormData((prev) => ({
            ...prev,
            book_pdf: null,
            book_pdf_original_name: "",
            uploaded_file_path: "",
          }));
          setErrors((prev) => ({
            ...prev,
            book_pdf:
              t?.fileUploadFailed || "Failed to upload file. Please try again.",
          }));
          setFileInputKey(Date.now());
        }
      } catch (error) {
        console.error("Book PDF upload error:", error);
        setFormData((prev) => ({
          ...prev,
          book_pdf: null,
          book_pdf_original_name: "",
          uploaded_file_path: "",
        }));
        setErrors((prev) => ({
          ...prev,
          book_pdf:
            t?.fileUploadFailed || "Failed to upload file. Please try again.",
        }));
        setFileInputKey(Date.now());
      } finally {
        setFileUploading(false);
      }
    },
    [
      t,
      formData.className,
      formData.subjectName,
      formData.medium,
      formData.term,
    ],
  );

  // ==================== STEP 1 VALIDATION ====================
  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.class_id) {
      newErrors.class_id = t?.classRequired || "Class is required";
    }
    if (!formData.subject) {
      newErrors.subject = t?.subjectRequired || "Subject is required";
    }
    if (!formData.medium) {
      newErrors.medium =
        t?.subjectMediumRequired || "Subject Medium is required";
    }
    if (!formData.uploaded_file_path) {
      newErrors.book_pdf = t?.documentRequired || "Document is required";
    }
    if (!formData.content_start_page) {
      newErrors.content_start_page =
        t?.contentStartPageRequired || "Content Start Page is required";
    }
    if (!formData.content_end_page) {
      newErrors.content_end_page =
        t?.contentEndPageRequired || "Content End Page is required";
    }
    if (!formData.book_page_start) {
      newErrors.book_page_start =
        t?.bookFirstPageNoRequired || "Book First Page No. is required";
    }
    if (!formData.pdf_page_start) {
      newErrors.pdf_page_start =
        t?.pdfPageOfBookFirstPageNoRequired ||
        "PDF Page of Book First Page No. is required";
    }
    if (
      formData.content_start_page &&
      formData.content_end_page &&
      Number(formData.content_end_page) < Number(formData.content_start_page)
    ) {
      newErrors.content_end_page =
        t?.contentEndPageMustBeAfterStart ||
        "Content End Page must be greater than or equal to Content Start Page";
    }
    if (formData.book_page_start && Number(formData.book_page_start) < 1) {
      newErrors.book_page_start =
        t?.bookFirstPageNoMin || "Book First Page No. must be at least 1";
    }
    if (formData.pdf_page_start && Number(formData.pdf_page_start) < 1) {
      newErrors.pdf_page_start =
        t?.pdfPageNoMin || "PDF Page of Book First Page No. must be at least 1";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== GENERATE TABLE OF CONTENTS ====================
  const handleGenerate = async () => {
    if (!validateStep1()) return;

    setGenerateLoading(true);
    try {
      const payload = {
        client_id: client_id,
        school_id: Number(school_id),
        class_id: Number(formData.class_id),
        subject: Number(formData.subject),
        medium: formData.medium,
        term: formData.term || null,
        volume: formData.volume || null,
        content_start_page: Number(formData.content_start_page),
        content_end_page: Number(formData.content_end_page),
        book_page_start: Number(formData.book_page_start),
        pdf_page_start: Number(formData.pdf_page_start),
        uploaded_file_path: formData.uploaded_file_path,
        pdf_original_name: formData.book_pdf_original_name,
        file_size: null,
        total_pages: null,
        skip_pages: null,
        total_chapters: null,
        note: null,
        is_active: 1,
        created_at: "null",
        user_id: String(user_id),
      };

      const response = await AxiosInstance.post(
        "/book/content-generate",
        payload,
      );

      if (response?.data?.success) {
        const resData = response.data;

        // If Gemini failed, show the error in a popup and stay on step 1
        if (resData.gemini_error) {
          if (resData.book_id) {
            await cancelBookApi(resData.book_id);
          }
          setModalState({
            open: true,
            type: "Failed",
            message:
              typeof resData.gemini_error === "string" &&
              resData.gemini_error.trim() !== ""
                ? resData.gemini_error
                : t?.saveFaileds ||
                  "AI content generation failed. Please try again.",
            redirectPath: "",
          });
          return;
        }

        // Keep "month" exactly as returned by the API so the dropdown pre-selects it
        setTocData(resData.toc_items || []);
        setBookId(resData.book_id || null);
        setActiveStep(2);
        setCompletedSteps([1]);
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message: t?.saveFaileds || response?.data.message,
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Generate TOC error:", error);
      setModalState({
        open: true,
        type: "Failed",
        message: t?.somethingWrong || "Something went wrong!",
        redirectPath: "",
      });
    } finally {
      setGenerateLoading(false);
    }
  };

  // ==================== TOC EDIT HANDLERS ====================
  const handleTocChange = (index, field, value) => {
    setTocData((prev) => {
      const updated = [...prev];
      if (field === "start_page" || field === "end_page") {
        if (value !== "" && !/^\d{0,4}$/.test(value)) return prev;
        updated[index] = {
          ...updated[index],
          [field]: value === "" ? "" : Number(value),
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });

    // Clear this row's month error once a month is selected
    if (field === "month" && value) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`month_${index}`];
        return next;
      });
    }
  };

  // Every topic row must have a month selected
  const validateMonths = () => {
    const monthErrors = {};
    (tocData || []).forEach((item, index) => {
      if (!item.month) {
        monthErrors[`month_${index}`] =
          t?.monthRequired || "Please select a month";
      }
    });
    return monthErrors;
  };

  // ==================== SUBMIT (CONTENT SAVE) ====================
  const handleSubmit = async () => {
    if (!tocData || tocData.length === 0) {
      setModalState({
        open: true,
        type: "Failed",
        message: t?.noRecordsToDisplay || "No records to display",
        redirectPath: "",
      });
      return;
    }

    const monthErrors = validateMonths();
    if (Object.keys(monthErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...monthErrors }));
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        book_id: bookId,
        school_id: Number(school_id),
        user_id: Number(user_id),
        client_id: client_id,
        toc_items: tocData.map((item) => ({
          topic: item.topic,
          sub_topic: item.sub_topic,
          start_page: Number(item.start_page),
          end_page: Number(item.end_page),
          month: item.month || null,
          month_end_date: item.month_end_date || null,
        })),
      };

      const response = await AxiosInstance.post("/book/content-save", payload);

      if (response?.data?.success || response?.data?.status) {
        isSubmittedRef.current = true;
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            t?.createSuccess ||
            "Created successfully!",
          redirectPath: "/user/bookList",
        });
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message:
            response?.data?.message ||
            t?.saveFailed ||
            "Failed to save. Please try again.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Content save error:", error);
      setModalState({
        open: true,
        type: "Failed",
        message: t?.somethingWrong || "Something went wrong!",
        redirectPath: "",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // ==================== BACK TO STEP 1 ====================
  const handleBackToAddBook = async () => {
    if (bookId) {
      const result = await cancelBookApi(bookId);
      if (result.success) {
        isSubmittedRef.current = true;
        bookIdRef.current = null;
        setBookId(null);

        window.history.back();
        setTimeout(() => {
          isSubmittedRef.current = false;
          setActiveStep(1);
          setCompletedSteps([]);
        }, 50);
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message: result.message || t?.cancelFailed || "Unable to go back.",
          redirectPath: "/user/bookList",
        });
      }
    } else {
      setActiveStep(1);
      setCompletedSteps([]);
    }
  };

  // ==================== CANCEL ====================
  const handleCancel = () => navigate(-1);

  // ==================== CANCEL STEP 2 ====================
  const handleCancelStep2 = async () => {
    if (bookId) {
      const result = await cancelBookApi(bookId);
      if (result.success) {
        bookIdRef.current = null;
        isSubmittedRef.current = true;
        setBookId(null);

        window.history.back();
        setTimeout(() => navigate(-1), 50);
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message: result.message || t?.cancelFailed || "Unable to cancel.",
          redirectPath: "/user/bookList",
        });
      }
    } else {
      navigate(-1);
    }
  };

  // ==================== VIEW UPLOADED BOOK (MODAL) ====================
  const handleViewUploadedBook = () => {
    if (formData.uploaded_file_path) {
      setPdfModalUrl(`${IMAGE_BASE_URL}${formData.uploaded_file_path}`);
      setPdfModalTitle(
        formData.book_pdf_original_name ||
          t?.viewUploadedBook ||
          "View Uploaded Book",
      );
      setPdfModalOpen(true);
    }
  };

  const handlePdfModalClose = () => {
    setPdfModalOpen(false);
    setPdfModalUrl("");
    setPdfModalTitle("");
  };

  // ==================== RENDER STEP 1: ADD BOOK ====================
  const renderStep1 = () => (
    <>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaBook />
        </div>
        <h1 className={styles.headerTitle}>{t?.addBook || "Add Book"}</h1>
      </div>

      <div className="row">
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.class || "Class"}
            name="class_id"
            value={formData.class_id}
            onChange={(value) => handleDropdownChange("class_id", value)}
            options={classOptions}
            placeholder={t?.selectClass || "Select Class"}
            required
            error={errors.class_id}
          />
        </div>

        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.subject || "Subject"}
            name="subject"
            value={formData.subject}
            onChange={(value) => handleDropdownChange("subject", value)}
            options={subjectOptions}
            placeholder={
              !formData.class_id
                ? t?.selectClassFirst || "Select Class First"
                : t?.selectsubjectPlaceholder || "Select subject"
            }
            required
            error={errors.subject}
            disabled={!formData.class_id}
          />
        </div>

        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.subjectMedium || "Subject Medium"}
            name="medium"
            value={formData.medium}
            onChange={(value) => handleDropdownChange("medium", value)}
            options={mediumOptions}
            placeholder={t?.selectMedium || "Select Medium"}
            required
            error={errors.medium}
          />
        </div>

        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.term || "Term"}
            name="term"
            value={formData.term}
            onChange={(value) => handleDropdownChange("term", value)}
            options={termOptions}
            placeholder={t?.selectTerm || "Select Term"}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={t?.volume || "Volume"}
            name="volume"
            value={formData.volume}
            onChange={(value) => handleDropdownChange("volume", value)}
            options={volumeOptions}
            placeholder={t?.selectVolume || "Select Volume"}
          />
        </div>

        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={t?.contentStartPage || "Content Start Page"}
            name="content_start_page"
            type="text"
            value={formData.content_start_page}
            onChange={handleInputChange}
            placeholder={t?.enterContentStartPage || "Enter Content Start Page"}
            maxLength={4}
            required
            error={errors.content_start_page}
          />
        </div>

        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={t?.contentEndPage || "Content End Page"}
            name="content_end_page"
            type="text"
            value={formData.content_end_page}
            onChange={handleInputChange}
            placeholder={t?.enterContentEndPage || "Enter Content End Page"}
            maxLength={4}
            required
            error={errors.content_end_page}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={t?.bookFirstPageNo || "Book First Page No."}
            name="book_page_start"
            type="text"
            value={formData.book_page_start}
            onChange={handleInputChange}
            placeholder="1"
            maxLength={4}
            required
            error={errors.book_page_start}
            helperText={
              t?.printedPageOnFirstContentPage ||
              "Printed page number on first content page"
            }
          />
        </div>

        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomTextInput
            label={
              t?.pdfPageOfBookFirstPageNo || "PDF Page of Book First Page No."
            }
            name="pdf_page_start"
            type="text"
            value={formData.pdf_page_start}
            onChange={handleInputChange}
            placeholder={t?.enterBookPDFNumber || "Enter Book PDF Number"}
            maxLength={4}
            required
            error={errors.pdf_page_start}
            helperText={
              t?.openPdfInViewer ||
              "Open PDF in viewer, find the page showing above number, enter that PDF page number"
            }
          />
        </div>

        <div className="col-lg-6 px-2 col-md-6 col-sm-12">
          <CustomFileInput
            key={fileInputKey}
            label={t?.uploadBookPdf || "Upload Book (PDF)"}
            name="book_pdf"
            value={formData.book_pdf_original_name || null}
            onChange={handleFileChange}
            accept=".pdf"
            placeholder={
              fileUploading
                ? t?.uploading || "Uploading..."
                : formData.book_pdf_original_name ||
                  t?.noFileChosen ||
                  "No file chosen"
            }
            buttonText={t?.chooseFile || "Choose File"}
            required
            error={errors.book_pdf}
            disabled={fileUploading}
          />
          {fileUploading && (
            <div className={styles.fileUploadingHint}>
              <span className={styles.smallSpinner} />
              <span>
                {t?.uploadingFile || "Uploading file, please wait..."}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.backBtnSmall}
          fullWidth={false}
          onClick={handleCancel}
        >
          {t?.cancel || "CANCEL"}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleGenerate}
          disabled={generateLoading || fileUploading}
        >
          {generateLoading
            ? t?.loading || "Loading..."
            : t?.generateTableOfContents || "GENERATE TABLE OF CONTENTS"}
        </CustomButton>
      </div>
    </>
  );

  // ==================== RENDER STEP 2: GENERATED TOC ====================
  const renderStep2 = () => (
    <>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaList />
        </div>
        <h1 className={styles.headerTitle}>
          {t?.generatedTableOfContents || "Generated Table of Contents"}
        </h1>
      </div>

      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t?.class || "Class"}</span>
          <span className={styles.infoValue}>{formData.className || "-"}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t?.subject || "Subject"}</span>
          <span className={styles.infoValue}>
            {formData.subjectName || "-"}
          </span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>
            {t?.subjectMedium || "Medium"}
          </span>
          <span className={styles.infoValue}>{formData.medium || "-"}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t?.term || "Term"}</span>
          <span className={styles.infoValue}>{formData.term || "-"}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>
            {t?.contentsPage || "Contents Page"}
          </span>
          <span className={styles.infoValue}>
            {formData.content_start_page || "0"} -{" "}
            {formData.content_end_page || "0"}
          </span>
        </div>
      </div>

      <div className={styles.topActions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleViewUploadedBook}
        >
          <FaEye style={{ marginRight: "8px" }} />
          {t?.viewUploadedBook || "VIEW UPLOADED BOOK"}
        </CustomButton>
      </div>

      <div className={styles.tocTableWrapper}>
        <table className={styles.tocTable}>
          <thead>
            <tr>
              <th>{t?.serialNo || "Serial No"}</th>
              <th>{t?.topic || "Topic"}</th>
              <th>{t?.subTopic || "Sub Topic"}</th>
              <th>
                {t?.start || "Start"} {t?.page || "Page"}
              </th>
              <th>
                {t?.end || "End"} {t?.page || "Page"}
              </th>
              <th>{t?.completionMonth || "Completion Month"}</th>
            </tr>
          </thead>
          <tbody>
            {tocData && tocData.length > 0 ? (
              tocData.map((item, index) => (
                <tr key={item.id ?? index} className={styles.tocRow}>
                  <td>{index + 1}</td>
                  <td>
                    <input
                      type="text"
                      value={item.topic || ""}
                      onChange={(e) =>
                        handleTocChange(index, "topic", e.target.value)
                      }
                      style={tocInputStyle}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.sub_topic || ""}
                      placeholder="-"
                      onChange={(e) =>
                        handleTocChange(index, "sub_topic", e.target.value)
                      }
                      style={tocInputStyle}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.start_page}
                      onChange={(e) =>
                        handleTocChange(index, "start_page", e.target.value)
                      }
                      style={tocNumInputStyle}
                      maxLength={4}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.end_page}
                      onChange={(e) =>
                        handleTocChange(index, "end_page", e.target.value)
                      }
                      style={tocNumInputStyle}
                      maxLength={4}
                    />
                  </td>
                  <td>
                    <CustomDropdown
                      name={`month_${index}`}
                      value={item.month || ""}
                      onChange={(selectedOption) =>
                        handleTocChange(
                          index,
                          "month",
                          selectedOption?.value || "",
                        )
                      }
                      options={monthOptions}
                      placeholder={t?.selectMonth || "Select Month"}
                      error={errors[`month_${index}`]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className={styles.noRecords}>
                  {t?.noRecordsToDisplay || "No records to display"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.backBtnSmall}
          fullWidth={false}
          onClick={handleCancelStep2}
        >
          {t?.cancel || "CANCEL"}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          className={styles.backBtnSmall}
          fullWidth={false}
          onClick={handleBackToAddBook}
        >
          <FaArrowLeft style={{ marginRight: "8px" }} />
          {t?.backToAddBook || "BACK TO ADD BOOK"}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleSubmit}
          disabled={submitLoading}
        >
          {submitLoading
            ? t?.submitting || "Submitting..."
            : t?.submit || "SUBMIT"}
        </CustomButton>
      </div>
    </>
  );

  // ==================== MAIN RENDER ====================
  return (
    <div className={styles.container}>
      <CustomProgressBar
        steps={stepperSteps}
        activeStep={activeStep}
        completedSteps={completedSteps}
      />

      {activeStep === 1 && renderStep1()}
      {activeStep === 2 && renderStep2()}

      {fileUploading && (
        <div className={styles.uploadOverlay}>
          <div className={styles.uploadOverlayBox}>
            <div className={styles.loader}></div>
            <p className={styles.uploadOverlayText}>
              {t?.uploadingFile || "Uploading file, please wait..."}
            </p>
          </div>
        </div>
      )}

      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? translations?.labels?.success || "Success"
            : modalState.type || translations?.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />

      <PDFModal
        show={pdfModalOpen}
        onClose={handlePdfModalClose}
        pdfUrl={pdfModalUrl}
        modalTitle={pdfModalTitle}
        translations={translations}
      />
    </div>
  );
};

export default BookCreate;
